import { useRef, useState } from "react";
import { playerIdFromMatchPerson, useOpenPlayerProfile } from "./useOpenPlayerProfile";
import type { IMatchDetails, InningScorecard, IPlayersTeamSquad } from "./types/matchDetails.d";
import VideoHighlights from "./VideoHighlights";
import type { IVideoHighlight } from "./VideoHighlights";
import { getSeasonYear } from "../../../../../../helper/helper";
import { getMatchDaysText, getMatchHoursOfPlay, getMatchOfficialDisplayName, getMatchPersonDisplayName, getMatchVenueText, getMatchStatusDisplayLabel } from "./matchDetailDisplay";

function formatInningOvers(over: number | undefined | null, ball: number | undefined | null): string {
  if (over == null && ball == null) return "-";
  return `${Number(over ?? 0)}.${Number(ball ?? 0)}`;
}

function formatInningScore(runs: number | undefined | null, wicket: number | undefined | null): string {
  if (runs == null && wicket == null) return "-";
  return `${runs ?? 0}/${wicket ?? 0}`;
}

interface ScoreCardProps {
  matchSummary: IMatchDetails | undefined;
  scores: InningScorecard[] | undefined;
  videos?: IVideoHighlight[]; // Optional videos array
  teamOnePlayers?: IPlayersTeamSquad;
  teamTwoPlayers?: IPlayersTeamSquad;
}

const MatchesScoreCard = ({ matchSummary, scores, videos, teamOnePlayers, teamTwoPlayers }: ScoreCardProps) => {
  const openPlayerProfile = useOpenPlayerProfile();
  const venueText = getMatchVenueText(matchSummary);

  const renderLinkedPlayerNameTd = (playerId: string | undefined, firstName?: string | null, lastName?: string | null) => {
    const label = [firstName, lastName].filter((x) => x?.trim()).join(" ") || "-";
    const id = playerId?.trim();
    if (!id) {
      return <td className="player-name">{label}</td>;
    }
    return (
      <td className="player-name">
        <button type="button" className="player-name-link" onClick={() => openPlayerProfile(id)}>
          {label}
        </button>
      </td>
    );
  };

  const squadOne = teamOnePlayers?.playingSquad ?? [];
  const squadTwo = teamTwoPlayers?.playingSquad ?? [];

  // ── Resolve which squad is batting in a given inning ──────────────────────
  // We can't rely on position (scores[0] = teamOne) because whichever team wins
  // the toss bats first. Instead we count player-ID overlaps between the inning's
  // batting list and each squad, and pick the better-matching one.
  // Fallback: compare battingTeamName to the match summary team names.
  const resolveSquadForInning = (inning: (typeof scores)[0] | undefined) => {
    if (!inning) return undefined;

    const batterIds = new Set<string>([...(inning.battingScoreCardResponseList?.map((b) => b.playerId) ?? []), inning.striker?.playerId, inning.nonStriker?.playerId].filter((id): id is string => !!id));

    if (batterIds.size > 0) {
      const oneMatches = squadOne.filter((p) => batterIds.has(p.playerId)).length;
      const twoMatches = squadTwo.filter((p) => batterIds.has(p.playerId)).length;
      // If one squad clearly matches more batters, use it
      if (oneMatches !== twoMatches) {
        return oneMatches > twoMatches ? squadOne : squadTwo;
      }
    }

    // Fallback: name comparison (covers innings not yet started)
    const battingName = inning.battingTeamName?.trim().toLowerCase();
    const nameOne = matchSummary?.teamOne?.teamName?.trim().toLowerCase();
    return battingName && nameOne && battingName === nameOne ? squadOne : squadTwo;
  };

  // ── Helper: compute "yet to bat / did not bat" players for a given inning ──
  const getUnsettledBatters = (inning: (typeof scores)[0] | undefined) => {
    const squad = resolveSquadForInning(inning);
    if (!squad?.length || !inning) return [];
    const isAllOut = (inning.wicket ?? 0) >= 10;
    if (isAllOut) return [];

    // Build set of player IDs who have already batted or are currently at crease
    const activeBattedIds = new Set<string>([...(inning.battingScoreCardResponseList?.map((b) => b.playerId) ?? []), inning.striker?.playerId, inning.nonStriker?.playerId].filter((id): id is string => !!id));

    return squad.filter((p) => p.playerId && !activeBattedIds.has(p.playerId));
  };

  const isInningLive = (inning: (typeof scores)[0] | undefined) => inning?.inningStatus === "IN_PROGRESS" || inning?.inningStatus === "STARTED";

  const teamOneInning = scores?.[0];
  const teamTwoInning = scores?.[1];
  const teamOneUnsettled = getUnsettledBatters(teamOneInning);
  const teamTwoUnsettled = getUnsettledBatters(teamTwoInning);
  // Static mock videos for testing - TODO: Remove when real videos are available
  const mockVideos: IVideoHighlight[] = videos || [
    {
      id: "1",
      thumbnail: "https://vid.cdn.nvplay.net/dd2ab83f-9542-44f7-91ec-bea7e9de730c/2_034_02.jpg",
      duration: "0.20",
      title: "Match Drawn - WCS win on first innings J Keenan c A Brier b P Leipst",
      videoUrl: "https://vid.cdn.nvplay.net/dd2ab83f-9542-44f7-91ec-bea7e9de730c/2_034_02.mp4",
    },
    {
      id: "2",
      thumbnail: "https://vid.cdn.nvplay.net/dd2ab83f-9542-44f7-91ec-bea7e9de730c/2_034_02.jpg",
      duration: "0.20",
      title: "Match Drawn - WCS win on first innings J Keenan c A Brier b P Leipst",
      videoUrl: "https://vid.cdn.nvplay.net/dd2ab83f-9542-44f7-91ec-bea7e9de730c/2_034_02.mp4",
    },
    {
      id: "3",
      thumbnail: "https://vid.cdn.nvplay.net/dd2ab83f-9542-44f7-91ec-bea7e9de730c/2_034_02.jpg",
      duration: "0.20",
      title: "Match Drawn - WCS win on first innings J Keenan c A Brier b P Leipst",
      videoUrl: "https://vid.cdn.nvplay.net/dd2ab83f-9542-44f7-91ec-bea7e9de730c/2_034_02.mp4",
    },
    {
      id: "4",
      thumbnail: "https://vid.cdn.nvplay.net/dd2ab83f-9542-44f7-91ec-bea7e9de730c/2_034_02.jpg",
      duration: "0.20",
      title: "Match Drawn - WCS win on first innings J Keenan c A Brier b P Leipst",
      videoUrl: "https://vid.cdn.nvplay.net/dd2ab83f-9542-44f7-91ec-bea7e9de730c/2_034_02.mp4",
    },
  ];

  const hasVideos = mockVideos && mockVideos.length > 0;
  const [activeTab, setActiveTab] = useState<number>(0);
  const firstTeamRef = useRef<HTMLDivElement>(null);
  const secondTeamRef = useRef<HTMLDivElement>(null);
  const matchFlowRef = useRef<HTMLDivElement>(null);
  const matchDetailsRef = useRef<HTMLDivElement>(null);

  const handleTabClick = (tabIndex: number) => {
    setActiveTab(tabIndex);

    let targetElement: HTMLElement | null = null;

    switch (tabIndex) {
      case 0:
        // First tab - scroll to first team-wrapper
        targetElement = firstTeamRef.current;
        break;
      case 1:
        // Second tab - scroll to second-team
        targetElement = secondTeamRef.current;
        break;
      case 2:
        // Third tab - scroll to match-flow-wrap
        targetElement = matchFlowRef.current;
        break;
      case 3:
        // Fourth tab - scroll to match-details-wrap
        targetElement = matchDetailsRef.current;
        break;
    }

    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  if (!scores?.length) {
    return null;
  }
  return (
    <>
      <div className={`matches-scorecard-wrapper ${hasVideos ? "with-videos" : ""}`}>
        <div className="scorecard-content-wrapper">
          <div className={`scorecard-main ${hasVideos ? "has-videos" : ""}`}>
            <div className="scorecard-tabs">
              <div className={`tab-item ${activeTab === 0 ? "active" : ""}`} onClick={() => handleTabClick(0)}>
                <p>{matchSummary?.teamOne?.teamCode}</p>
              </div>
              <div className={`tab-item ${activeTab === 1 ? "active" : ""}`} onClick={() => handleTabClick(1)}>
                <p>{matchSummary?.teamTwo?.teamCode}</p>
              </div>
              {/* <div
                className={`tab-item ${activeTab === 2 ? 'active' : ''}`}
                onClick={() => handleTabClick(2)}
              >
                <p>Match Details</p>
              </div> */}
              <div className={`tab-item ${activeTab === 3 ? "active" : ""}`} onClick={() => handleTabClick(3)}>
                <p>Match Details</p>
              </div>
            </div>
            <div className="team-wrap" ref={firstTeamRef}>
              <div className="team-wrapper">
                <p className="nav-right-text">
                  <span className="text-wrapper">{scores[0]?.battingTeamName ?? "-"} </span>
                  <span className="over-span">({scores[0]?.maxOvers ?? "-"} Ovs Maximum)</span>
                </p>
                <p className="nav-left-text">
                  <span className="text-wrapper-2">Overs ({formatInningOvers(scores[0]?.over, scores[0]?.ball)})</span>
                  <span className="text-wrapper-3">&nbsp;</span>
                  <span className="text-wrapper-4"> {formatInningScore(scores[0]?.runs, scores[0]?.wicket)}</span>
                </p>
              </div>
              <div className="table-wrapper">
                <table className="stats-table">
                  <thead>
                    <tr className="header-row">
                      <th>Batting</th>
                      <th></th>
                      <th>R</th>
                      <th>B</th>
                      <th>4s</th>
                      <th>6s</th>
                      <th>SR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scores[0]?.battingScoreCardResponseList?.map((item) => (
                      <tr key={item.id}>
                        {renderLinkedPlayerNameTd(item?.playerId, item?.playerFirstName, item?.playerLastName)}
                        <td>{item?.outString ?? "-"}</td>
                        <td className="stats">{item?.run ?? "-"}</td>
                        <td className="stats">{item?.balls ?? "-"}</td>
                        <td className="stats">{item?.fours ?? "-"}</td>
                        <td className="stats">{item?.six ?? "-"}</td>
                        <td className="stats">{item?.strikeRate != null ? Number(item.strikeRate).toFixed(2) : "-"}</td>
                      </tr>
                    ))}
                    {(() => {
                      const s = scores[0];
                      if (!s) return null;
                      const nb = s.noBall ?? 0;
                      const w = s.wide ?? 0;
                      const b = s.bye ?? 0;
                      const lb = s.legBye ?? 0;
                      const pen = s.penalty ?? 0;
                      const total = nb + w + b + lb + pen;
                      return (
                        <tr className="extras-row">
                          <td className="player-name">Extras</td>
                          <td>{`( nb ${nb}, w ${w}, b ${b}, lb ${lb}, pen ${pen} )`}</td>
                          <td className="stats">{total}</td>
                          <td colSpan={4}></td>
                        </tr>
                      );
                    })()}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td>Total</td>
                      <td>
                        {formatInningOvers(scores[0]?.over, scores[0]?.ball)} Ov (CRR: {scores[0]?.currentRunrate != null ? Number(scores[0].currentRunrate).toFixed(2) : "-"})
                      </td>
                      <td colSpan={5}>{scores[0]?.runs ?? "-"}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="fall-wickets">
                <p className="nav-text">
                  <span className="text-wrapper">Fall Of Wickets:</span>
                  <span className="span"> </span>
                  <span className="span">{scores[0]?.fallOfWickets && (scores[0].fallOfWickets as any[]).length > 0 ? (scores[0].fallOfWickets as any[]).join(", ") : "-"}</span>
                </p>
                {teamOneUnsettled.length > 0 && (
                  <div className="did-not-bat">
                    <p className="nav-text">
                      <span className="text-wrapper">{isInningLive(teamOneInning) ? "Yet to Bat:" : "Did Not Bat:"}</span>
                      <span className="span">
                        {" "}
                        {teamOneUnsettled.map((p, i) => (
                          <span key={p.playerId || `t1-ytb-${i}`}>
                            {i > 0 ? ", " : ""}
                            {p.playerId?.trim() ? (
                              <button type="button" className="player-name-link" onClick={() => openPlayerProfile(p.playerId)}>
                                {`${p.playerFirstName ?? ""} ${p.playerLastName ?? ""}`.trim()}
                              </button>
                            ) : (
                              `${p.playerFirstName ?? ""} ${p.playerLastName ?? ""}`.trim()
                            )}
                          </span>
                        ))}
                      </span>
                    </p>
                  </div>
                )}
              </div>
              <div className="table-wrapper">
                <table className="stats-table bowling-stats-table">
                  <thead>
                    <tr className="header-row">
                      <th>Bowling</th>
                      <th>O</th>
                      <th>M</th>
                      <th>R</th>
                      <th>W</th>
                      <th>Econ.</th>
                      <th>Dots</th>
                      <th>WD</th>
                      <th>NB</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scores[0]?.bowlingScoreCardResponses?.map((item) => (
                      <tr key={item.id}>
                        {renderLinkedPlayerNameTd(item?.playerId, item?.playerFirstName, item?.playerLastName)}
                        <td className="stats">{item?.over != null ? `${item.over}.${item?.ball ?? 0}` : "-"}</td>
                        <td className="stats">{item?.maiden ?? "-"}</td>
                        <td className="stats">{item?.runs ?? "-"}</td>
                        <td className="stats">{item?.wickets ?? "-"}</td>
                        <td className="stats">{item?.economy != null ? Number(item.economy).toFixed(2) : "-"}</td>
                        <td className="stats">{item?.dotBall ?? "-"}</td>
                        <td className="stats">{item?.wide ?? "-"}</td>
                        <td className="stats">{item?.noBall ?? "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="team-wrap second-team" ref={secondTeamRef}>
              <div className="team-wrapper">
                <p className="nav-right-text">
                  <span className="text-wrapper">{scores[1]?.battingTeamName ?? "-"} </span>
                  <span className="over-span">({scores[1]?.maxOvers ?? "-"} Ovs Maximum)</span>
                </p>
                <p className="nav-left-text">
                  <span className="text-wrapper-2">Overs ({formatInningOvers(scores[1]?.over, scores[1]?.ball)})</span>
                  <span className="text-wrapper-3">&nbsp;</span>
                  <span className="text-wrapper-4"> {formatInningScore(scores[1]?.runs, scores[1]?.wicket)}</span>
                </p>
              </div>
              <div className="table-wrapper">
                <table className="stats-table">
                  <thead>
                    <tr className="header-row">
                      <th>Batting</th>
                      <th></th>
                      <th>R</th>
                      <th>B</th>
                      <th>4s</th>
                      <th>6s</th>
                      <th>SR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scores[1]?.battingScoreCardResponseList?.map((item) => (
                      <tr key={item.id}>
                        {renderLinkedPlayerNameTd(item?.playerId, item?.playerFirstName, item?.playerLastName)}
                        <td>{item?.outString ?? "-"}</td>
                        <td className="stats">{item?.run ?? "-"}</td>
                        <td className="stats">{item?.balls ?? "-"}</td>
                        <td className="stats">{item?.fours ?? "-"}</td>
                        <td className="stats">{item?.six ?? "-"}</td>
                        <td className="stats">{item?.strikeRate != null ? Number(item.strikeRate).toFixed(2) : "-"}</td>
                      </tr>
                    ))}
                    {(() => {
                      const s = scores[1];
                      if (!s) return null;
                      const nb = s.noBall ?? 0;
                      const w = s.wide ?? 0;
                      const b = s.bye ?? 0;
                      const lb = s.legBye ?? 0;
                      const pen = s.penalty ?? 0;
                      const total = nb + w + b + lb + pen;
                      return (
                        <tr className="extras-row">
                          <td className="player-name">Extras</td>
                          <td>{`( nb ${nb}, w ${w}, b ${b}, lb ${lb}, pen ${pen} )`}</td>
                          <td className="stats">{total}</td>
                          <td colSpan={4}></td>
                        </tr>
                      );
                    })()}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td>Total</td>
                      <td>
                        {formatInningOvers(scores[1]?.over, scores[1]?.ball)} Ov (CRR: {scores[1]?.currentRunrate != null ? Number(scores[1].currentRunrate).toFixed(2) : "-"})
                      </td>
                      <td colSpan={5}>{scores[1]?.runs ?? "-"}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="fall-wickets">
                <p className="nav-text">
                  <span className="text-wrapper">Fall Of Wickets:</span>
                  <span className="span">{scores[1]?.fallOfWickets && (scores[1].fallOfWickets as any[]).length > 0 ? (scores[1].fallOfWickets as any[]).join(", ") : "-"} </span>
                  {/* <span className="text-wrapper-2">1-15</span>
              <span className="span"> (moeen Ali, 3.4 Ov), </span>
              <span className="text-wrapper-2">2-26</span>
              <span className="span"> (ben Mcdermott, 5.2 Ov), </span>
              <span className="text-wrapper-2">3-26</span>
              <span className="span"> (shimron Hetmyer, 5.5 Ov), </span>
              <span className="text-wrapper-2">4-50</span>
              <span className="span"> (hassan Khan, 8.4 Ov), </span>
              <span className="text-wrapper-2">5-54</span>
              <span className="span"> (shai Hope, 8.6 Ov), </span>
              <span className="text-wrapper-2">6-58</span>
              <span className="span"> (gudakesh Motie, 9.2 Ov), </span>
              <span className="text-wrapper-2">7-73</span>
              <span className="span"> (keemo Paul, 12.3 Ov), </span>
              <span className="text-wrapper-2">8-77</span>
              <span className="span"> (romario Shepherd, 14.2 Ov), </span>
              <span className="text-wrapper-2">9-78</span>
              <span className="span"> (dwaine Pretorius, 14.5 Ov), </span>
              <span className="text-wrapper-2">10-99</span>
              <span className="span"> (quentin Sampson, 18.1 Ov) • drs</span> */}
                </p>
                {teamTwoUnsettled.length > 0 && (
                  <div className="did-not-bat">
                    <p className="nav-text">
                      <span className="text-wrapper">{isInningLive(teamTwoInning) ? "Yet to Bat:" : "Did Not Bat:"}</span>
                      <span className="span">
                        {" "}
                        {teamTwoUnsettled.map((p, i) => (
                          <span key={p.playerId || `t2-ytb-${i}`}>
                            {i > 0 ? ", " : ""}
                            {p.playerId?.trim() ? (
                              <button type="button" className="player-name-link" onClick={() => openPlayerProfile(p.playerId)}>
                                {`${p.playerFirstName ?? ""} ${p.playerLastName ?? ""}`.trim()}
                              </button>
                            ) : (
                              `${p.playerFirstName ?? ""} ${p.playerLastName ?? ""}`.trim()
                            )}
                          </span>
                        ))}
                      </span>
                    </p>
                  </div>
                )}
              </div>
              <div className="table-wrapper">
                <table className="stats-table bowling-stats-table">
                  <thead>
                    <tr className="header-row">
                      <th>Bowling</th>
                      <th>O</th>
                      <th>M</th>
                      <th>R</th>
                      <th>W</th>
                      <th>Econ.</th>
                      <th>Dots</th>
                      <th>4s</th>
                      <th>6s</th>
                      <th>WD</th>
                      <th>NB</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scores[1]?.bowlingScoreCardResponses?.map((item) => (
                      <tr key={item.id}>
                        {renderLinkedPlayerNameTd(item?.playerId, item?.playerFirstName, item?.playerLastName)}
                        <td className="stats">{item?.over != null ? `${item.over}.${item?.ball ?? 0}` : "-"}</td>
                        <td className="stats">{item?.maiden ?? "-"}</td>
                        <td className="stats">{item?.runs ?? "-"}</td>
                        <td className="stats">{item?.wickets ?? "-"}</td>
                        <td className="stats">{item?.economy != null ? Number(item.economy).toFixed(2) : "-"}</td>
                        <td className="stats">{item?.dotBall ?? "-"}</td>
                        <td className="stats">{item?.fours ?? "-"}</td>
                        <td className="stats">{item?.six ?? "-"}</td>
                        <td className="stats">{item?.wide ?? "-"}</td>
                        <td className="stats">{item?.noBall ?? "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* <div className="match-flow-wrap" ref={matchFlowRef}>
              <div className="team-wrapper">
                <p className="nav-right-text">
                  <span className="text-wrapper">Match Flow</span>
                </p>
              </div>
              <div className="match-flow-data-wrap">
                <ul className="team-one">
                  <li className="text-wrapper">Sunrisers Hyderabad</li>
                  <li className="span">
                    Powerplay 1: Overs 0.1 - 6.0{" "}
                    <span className="score">(Srh: 71/2)</span>{" "}
                  </li>
                  <li className="span">
                    Sunrisers Hyderabad: 50 Runs In 5.5 Overs (51 Balls), Extras 0
                  </li>
                  <li className="span">
                    Strategic Timeout: Guyana Amazon Warriors - 58/6 In 9.2 Overs (d
                    Pretorius 0)
                  </li>
                  <li className="span">
                    Guyana Amazon Warriors: 100 Runs In 17.4 Overs (106 Balls),
                    Extras 0
                  </li>
                  <li className="span">
                    Innings Break: Guyana Amazon Warriors - 99/10 In 18.1 Overs
                    (imran Tahir 3)
                  </li>
                </ul>
                <ul className="team-two">
                  <li className="text-wrapper">Royal Challengers</li>
                  <li className="span">
                    Powerplay 1: Overs 0.1 - 6.0{" "}
                    <span className="score">(Rcb: 71/2)</span>{" "}
                  </li>
                  <li className="span">
                    Over 3.2: Review By Antigua And Barbuda Falcons (batting),
                    Decision Challenged - Wicket, Umpire - Z Bassarath, Batter - Ko
                    Wickham (struck Down)
                  </li>
                  <li className="span">
                    Over 3.3: Review By Guyana Amazon Warriors (bowling), Decision
                    Challenged - Wicket, Umpire - Z Bassarath, Batter - K Gore
                    (upheld)
                  </li>
                  <li className="span">
                    Over 4.1: Review By Guyana Amazon Warriors (bowling), Decision
                    Challenged - Wicket, Umpire - Ls Reifer, Batter - Shakib Al
                    Hasan (upheld)
                  </li>
                  <li className="span">
                    Antigua And Barbuda Falcons: 50 Runs In 9.4 Overs (58 Balls),
                    Extras 3
                  </li>
                  <li className="span">
                    Antigua And Barbuda Falcons: 50 Runs In 9.5 Overs (59 Balls),
                    Extras 3
                  </li>
                  <li className="span">
                    Strategic Timeout: Antigua And Barbuda Falcons - 50/5 In 10.0
                    Overs (aa Jangoo 20, Imad Wasim 10)
                  </li>
                  <li className="span">
                    aa Jangoo: 50 Off 54 Balls (3 X 4, 2 X 6)
                  </li>
                  <li className="span">
                    Antigua And Barbuda Falcons: 100 Runs In 19.1 Overs (115 Balls),
                    Extras 4
                  </li>
                </ul>
              </div>
            </div> */}
            <div className="match-details-wrap" ref={matchDetailsRef}>
              <div className="team-wrapper">
                <p className="nav-right-text">
                  <span className="text-wrapper">Match Details</span>
                </p>
              </div>
              <div className="location">
                <p>{venueText}</p>
              </div>
              <div className="match-details-table-wrap">
                <table>
                  <tbody>
                    <tr>
                      <th scope="row">Toss</th>
                      <td>{matchSummary?.tossResult}</td>
                    </tr>

                    <tr className="series-row">
                      <th scope="row">Series</th>
                      <td>{matchSummary?.tournament?.tournamentName}</td>
                    </tr>

                    <tr>
                      <th scope="row">Season</th>
                      <td className="is-strong">{getSeasonYear(matchSummary?.startDate)}</td>
                    </tr>

                    <tr>
                      <th scope="row">Player of The Match</th>
                      <td>
                        {(() => {
                          const potm = matchSummary?.playerOfTheMatch;
                          const pid = playerIdFromMatchPerson(potm);
                          const name = getMatchPersonDisplayName(potm);
                          if (!pid) return name;
                          return (
                            <button type="button" className="player-name-link" onClick={() => openPlayerProfile(pid)}>
                              {name}
                            </button>
                          );
                        })()}
                      </td>
                    </tr>

                    <tr>
                      <th scope="row">League</th>
                      <td>{matchSummary?.leagueName ?? "-"}</td>
                    </tr>

                    <tr>
                      <th scope="row">Hours of Play (local time)</th>
                      <td>{getMatchHoursOfPlay(matchSummary)}</td>
                    </tr>

                    <tr>
                      <th scope="row">Match days</th>
                      <td>{getMatchDaysText(matchSummary)}</td>
                    </tr>

                    <tr>
                      <th scope="row">Umpire</th>
                      <td>{getMatchOfficialDisplayName(matchSummary?.umpire ?? matchSummary?.availableUmpireResponse)}</td>
                    </tr>

                    <tr>
                      <th scope="row">Second Umpire</th>
                      <td>{getMatchOfficialDisplayName(matchSummary?.umpireTwo)}</td>
                    </tr>

                    <tr>
                      <th scope="row">Third Umpire</th>
                      <td>{getMatchOfficialDisplayName(matchSummary?.thirdUmpire)}</td>
                    </tr>

                    <tr>
                      <th scope="row">Match Status</th>
                      <td>{getMatchStatusDisplayLabel(matchSummary?.matchStatus)}</td>
                    </tr>

                    <tr>
                      <th scope="row">Result</th>
                      <td>{matchSummary?.matchResult ?? matchSummary?.equation ?? "-"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* {hasVideos && (
            <div className="video-highlights-wrapper">
              <VideoHighlights videos={mockVideos} />
            </div>
          )} */}
        </div>
      </div>
    </>
  );
};

export default MatchesScoreCard;
